import { useParams, Link } from 'react-router-dom';
import { useRef } from 'react';
import { courses } from '../../mocks/courses';

import { useProgress } from '../../context/ProgressContext';
import { getScoreLabel, getScoreBg } from '../../types';
import Navbar from '../../components/feature/Navbar';
import { useAuth } from '../../context/useAuth';

export default function CertificatePage() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const { getEnrollment, getQuizResult } = useProgress();
  const printRef = useRef(null);

  const course = courses.find(c => c.id === courseId);
  const enrollment = course ? getEnrollment(course.id) : undefined;
  const finalResult = course ? getQuizResult(course.finalQuiz.id) : undefined;

  const handlePrint = () => window.print();

  if (!course || !enrollment?.courseCompleted) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-4">
              <i className="ri-lock-line text-3xl text-amber-500" />
            </div>
            <p className="text-xl font-bold text-slate-800">Certificate Not Available</p>
            <p className="text-slate-500 text-sm mt-2 mb-5">Complete all lessons and quizzes to earn your certificate.</p>
            <Link to="/courses" className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white font-semibold rounded-xl cursor-pointer hover:bg-teal-700 whitespace-nowrap">
              Browse Courses
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const completedDate = enrollment.completedAt
    ? new Date(enrollment.completedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const allQuizResults = enrollment.quizResults;
  const avgScore = allQuizResults.length
    ? Math.round(allQuizResults.reduce((s, r) => s + r.score, 0) / allQuizResults.length)
    : 0;
  const finalScore = finalResult?.score ?? 0;
  const label = getScoreLabel(finalScore);

  return (
    <div className="min-h-screen bg-slate-100">
      <Navbar />
      <div className="pt-16">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <div className="flex items-center justify-between mb-6 print:hidden">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Certificate of Completion</h1>
              <p className="text-slate-500 text-sm mt-0.5">Your achievement is ready to share</p>
            </div>
            <div className="flex gap-3">
              <Link to="/dashboard" className="px-4 py-2 border border-slate-200 text-slate-600 text-sm font-medium rounded-xl cursor-pointer hover:bg-slate-50 whitespace-nowrap flex items-center gap-2">
                <i className="ri-arrow-left-line" /> Dashboard
              </Link>
              <button onClick={handlePrint} className="px-5 py-2 bg-teal-600 text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-teal-700 whitespace-nowrap flex items-center gap-2">
                <i className="ri-download-line" /> Download / Print
              </button>
            </div>
          </div>

          <div ref={printRef} className="bg-white rounded-2xl overflow-hidden border-4 border-teal-600">
            <div className="bg-teal-600 px-10 py-6 flex items-center justify-between">
              <img src="https://public.readdy.ai/ai/img_res/a37d52a8-201a-4b92-9b36-08215345d1df.png" alt="LearnFlow" className="h-10 w-auto brightness-200" />
              <div className="text-right">
                <p className="text-teal-200 text-xs uppercase tracking-widest">Certificate of</p>
                <p className="text-white font-bold text-lg">Completion</p>
              </div>
            </div>

            <div className="px-10 py-10 text-center">
              <p className="text-slate-500 text-sm uppercase tracking-widest mb-3">This is to certify that</p>
              <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Georgia, serif' }}>{user?.name}</h2>
              <div className="w-24 h-0.5 bg-teal-400 mx-auto mb-5" />
              <p className="text-slate-600 text-base mb-2">has successfully completed the course</p>
              <h3 className="text-2xl font-bold text-teal-700 mb-6">{course.title}</h3>

              <div className="flex items-center justify-center gap-8 mb-8">
                <div className="text-center">
                  <p className="text-3xl font-bold text-slate-900">{finalScore}%</p>
                  <p className="text-xs text-slate-400 mt-1">Final Quiz Score</p>
                </div>
                <div className="w-px h-12 bg-slate-200" />
                <div className="text-center">
                  <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-bold border ${getScoreBg(label)}`}>{label}</span>
                  <p className="text-xs text-slate-400 mt-1">Performance</p>
                </div>
                <div className="w-px h-12 bg-slate-200" />
                <div className="text-center">
                  <p className="text-3xl font-bold text-slate-900">{avgScore}%</p>
                  <p className="text-xs text-slate-400 mt-1">Average Score</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-8 bg-slate-50 rounded-xl p-4">
                <div className="text-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg mx-auto mb-1.5">
                    <i className="ri-book-open-line text-teal-600" />
                  </div>
                  <p className="text-sm font-bold text-slate-800">{course.lessons.length}</p>
                  <p className="text-xs text-slate-400">Lessons</p>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg mx-auto mb-1.5">
                    <i className="ri-quiz-line text-amber-600" />
                  </div>
                  <p className="text-sm font-bold text-slate-800">{allQuizResults.length}</p>
                  <p className="text-xs text-slate-400">Quizzes</p>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg mx-auto mb-1.5">
                    <i className="ri-time-line text-emerald-600" />
                  </div>
                  <p className="text-sm font-bold text-slate-800">{course.duration}</p>
                  <p className="text-xs text-slate-400">Duration</p>
                </div>
              </div>

              <div className="flex items-end justify-between pt-6 border-t border-slate-100">
                <div className="text-left">
                  <p className="font-bold text-slate-800 text-sm">{course.instructor}</p>
                  <div className="w-28 h-0.5 bg-slate-300 mt-1 mb-1" />
                  <p className="text-xs text-slate-400">Course Instructor</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-2 border-2 border-teal-200">
                    <i className="ri-award-fill text-teal-600 text-3xl" />
                  </div>
                  <p className="text-xs text-slate-400">Official Seal</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-slate-800 text-sm">{completedDate}</p>
                  <div className="w-28 h-0.5 bg-slate-300 mt-1 mb-1 ml-auto" />
                  <p className="text-xs text-slate-400">Date of Completion</p>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 px-10 py-4 flex items-center justify-between border-t border-slate-100">
              <p className="text-xs text-slate-400">Certificate ID: LF-{courseId?.toUpperCase()}-{user?.id?.slice(-6)?.toUpperCase()}</p>
              <p className="text-xs text-slate-400">learnflow.com · Verified Certificate</p>
            </div>
          </div>

          <div className="mt-6 bg-white rounded-xl border border-slate-100 p-5 print:hidden">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Your Performance Breakdown</h3>
            <div className="space-y-3">
              {allQuizResults.map(r => {
                const lesson = r.lessonId ? course.lessons.find(l => l.id === r.lessonId) : null;
                const lbl = getScoreLabel(r.score);
                return (
                  <div key={r.quizId} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                    <span className="text-sm text-slate-600">{r.isFinalQuiz ? 'Final Quiz' : lesson?.title || 'Quiz'}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${r.score >= 80 ? 'bg-emerald-500' : r.score >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${r.score}%` }} />
                      </div>
                      <span className="text-sm font-bold text-slate-800 w-10 text-right">{r.score}%</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${getScoreBg(lbl)}`}>{lbl}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
